import { useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { BookOpen, GitMerge, DollarSign, Settings2, Layers, FileText, ArrowRight, AlertTriangle } from 'lucide-react'
import { useErpDealNames, useErpAccAssignments } from '@/screens/Review/sections/InvoiceReviewSection/hooks/useErpOptions'

interface ClassificationCorrection {
  line_item_group: string
  description: string
  deal_name?: number
  acc_assignment?: number
  analytic_parts?: Array<{
    acc: string
    level1?: string
    level1_name?: string
    level2?: string
    level2_name?: string
    level3?: string
    level3_name?: string
  }>
  vat_rate?: number
  is_override?: boolean
  existing_rule_ids?: string[]
}

interface HeaderCorrection {
  field_type: string
  field_name: string
  original_value: string
  corrected_value: string
  is_override?: boolean
  existing_rule_id?: string
}

interface FieldOption {
  value: string
  label: string
}

interface FieldOptions {
  message_type?: FieldOption[]
  document_subject?: FieldOption[]
}

interface PendingLearnings {
  has_changes: boolean
  price_rules: Array<{
    rule_type: string
    description: string
    affected_items: number
    total_items: number
  }>
  merge_patterns: Array<{
    description: string
    source_groups: string[]
  }>
  classification_corrections: ClassificationCorrection[]
  header_corrections: HeaderCorrection[]
}

export interface OverrideDecisions {
  [ruleId: string]: 'replace' | 'skip'
}

interface LearnFromChangesDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (learn: boolean, rememberChoice: boolean, overrideDecisions?: OverrideDecisions) => void
  learnings: PendingLearnings
  isLoading?: boolean
  clientId?: string
  isIncome?: boolean
  fieldOptions?: FieldOptions
}

const HEADER_FIELD_LABELS: Record<string, string> = {
  message_type: 'Тип документ',
  additional_remarks: 'Предмет на документа',
}

function resolveHeaderLabel(fieldType: string, value: string, fieldOptions?: FieldOptions): string {
  if (!value) return '—'
  if (fieldType === 'message_type') {
    return fieldOptions?.message_type?.find(o => o.value === value)?.label ?? value
  }
  if (fieldType === 'additional_remarks') {
    return fieldOptions?.document_subject?.find(o => o.value === value)?.label ?? value
  }
  return value
}

function HeaderCorrectionRow({ correction, fieldOptions }: {
  correction: HeaderCorrection
  fieldOptions?: FieldOptions
}) {
  const label = HEADER_FIELD_LABELS[correction.field_type] ?? correction.field_name
  const oldLabel = resolveHeaderLabel(correction.field_type, correction.original_value, fieldOptions)
  const newLabel = resolveHeaderLabel(correction.field_type, correction.corrected_value, fieldOptions)

  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
      <span className="text-muted-foreground whitespace-nowrap">{label}</span>
      <span className="flex items-center gap-1.5 text-foreground/80 min-w-0">
        <span className="line-through text-muted-foreground/60 truncate" title={oldLabel}>{oldLabel}</span>
        <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground/40" />
        <span className="font-medium truncate" title={newLabel}>{newLabel}</span>
      </span>
    </div>
  )
}

function OverrideDecisionToggle({ ruleId, decision, onChange }: {
  ruleId: string
  decision: 'replace' | 'skip'
  onChange: (ruleId: string, decision: 'replace' | 'skip') => void
}) {
  return (
    <div className="inline-flex rounded-md border border-border/60 overflow-hidden mt-2 text-xs">
      <label
        className={`flex items-center gap-1.5 px-3 py-1.5 cursor-pointer select-none transition-colors ${
          decision === 'replace'
            ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
            : 'bg-background text-muted-foreground hover:bg-muted/50'
        }`}
      >
        <input
          type="radio"
          name={`override-${ruleId}`}
          checked={decision === 'replace'}
          onChange={() => onChange(ruleId, 'replace')}
          className="h-3 w-3 accent-blue-600"
        />
        Replace rule
      </label>
      <label
        className={`flex items-center gap-1.5 px-3 py-1.5 cursor-pointer select-none border-l border-border/60 transition-colors ${
          decision === 'skip'
            ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
            : 'bg-background text-muted-foreground hover:bg-muted/50'
        }`}
      >
        <input
          type="radio"
          name={`override-${ruleId}`}
          checked={decision === 'skip'}
          onChange={() => onChange(ruleId, 'skip')}
          className="h-3 w-3 accent-amber-600"
        />
        Just this time
      </label>
    </div>
  )
}

function ClassificationCard({ correction, dealNames, accAssignments }: {
  correction: ClassificationCorrection
  dealNames: Array<{ id: number; description: string }>
  accAssignments: Array<{ id: number; description: string }>
}) {
  const dealLabel = correction.deal_name != null
    ? dealNames.find(d => d.id === correction.deal_name)?.description ?? `#${correction.deal_name}`
    : null

  const accLabel = correction.acc_assignment != null
    ? accAssignments.find(a => a.id === correction.acc_assignment)?.description ?? `#${correction.acc_assignment}`
    : null

  const parts = correction.analytic_parts?.filter(p => p.acc) ?? []

  return (
    <div className="rounded-md border border-border/60 bg-muted/30 p-3 space-y-2">
      <div className="text-sm font-medium text-foreground leading-snug">
        {correction.description || correction.line_item_group}
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        {dealLabel && (
          <>
            <span className="text-muted-foreground whitespace-nowrap">Deal</span>
            <span className="text-foreground/80 truncate" title={dealLabel}>{dealLabel}</span>
          </>
        )}
        {accLabel && (
          <>
            <span className="text-muted-foreground whitespace-nowrap">Account</span>
            <span className="text-foreground/80 truncate" title={accLabel}>{accLabel}</span>
          </>
        )}
      </div>

      {parts.length > 0 && (
        <div className="space-y-1 pt-1 border-t border-border/40">
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground uppercase tracking-wider font-medium">
            <Layers className="h-3 w-3" />
            Analytics
          </div>
          <div className="flex flex-wrap gap-1.5">
            {parts.map((part, j) => {
              const levels = [
                part.level1_name || part.level1,
                part.level2_name || part.level2,
                part.level3_name || part.level3,
              ].filter(Boolean)
              if (levels.length === 0) return null
              return (
                <span
                  key={j}
                  className="inline-flex items-center gap-1 rounded bg-background border border-border/60 px-2 py-0.5 text-xs text-foreground/70"
                >
                  <span className="font-medium text-foreground/90">{part.acc}</span>
                  <span className="text-muted-foreground">·</span>
                  {levels.join(' → ')}
                </span>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function SectionCard({
  icon,
  title,
  accentColor,
  children,
}: {
  icon: React.ReactNode
  title: string
  accentColor: string
  children: React.ReactNode
}) {
  return (
    <div className={`rounded-lg border-l-[3px] ${accentColor} bg-muted/20 p-3 space-y-2`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground/90">
        {icon}
        {title}
      </div>
      {children}
    </div>
  )
}

export function LearnFromChangesDialog({
  open,
  onClose,
  onConfirm,
  learnings,
  isLoading,
  clientId,
  isIncome = false,
  fieldOptions,
}: LearnFromChangesDialogProps) {
  const [rememberChoice, setRememberChoice] = useState(false)
  const [overrideDecisions, setOverrideDecisions] = useState<OverrideDecisions>({})
  const { dealNames } = useErpDealNames(clientId, isIncome)
  const { accAssignments } = useErpAccAssignments(clientId, isIncome)

  const headerCorrections = learnings.header_corrections ?? []
  const freshHeaders = headerCorrections.filter(c => !c.is_override)
  const overrideHeaders = headerCorrections.filter(c => c.is_override)

  const classificationCorrections = learnings.classification_corrections ?? []
  const freshClassifications = classificationCorrections.filter(c => !c.is_override)
  const overrideClassifications = classificationCorrections.filter(c => c.is_override)

  const hasOverrides = overrideHeaders.length > 0 || overrideClassifications.length > 0
  const hasFreshChanges =
    learnings.price_rules.length > 0 ||
    learnings.merge_patterns.length > 0 ||
    freshClassifications.length > 0 ||
    freshHeaders.length > 0

  const totalChanges =
    learnings.price_rules.length +
    learnings.merge_patterns.length +
    freshClassifications.length +
    freshHeaders.length

  const handleOverrideDecision = (ruleId: string, decision: 'replace' | 'skip') => {
    setOverrideDecisions(prev => ({ ...prev, [ruleId]: decision }))
  }

  const getDecision = (ruleId: string): 'replace' | 'skip' => {
    return overrideDecisions[ruleId] ?? 'skip'
  }

  const allOverrideRuleIds = [
    ...overrideHeaders.map(c => c.existing_rule_id).filter(Boolean),
    ...overrideClassifications.flatMap(c => c.existing_rule_ids ?? []),
  ] as string[]

  const finalDecisions: OverrideDecisions = {}
  for (const ruleId of allOverrideRuleIds) {
    finalDecisions[ruleId] = getDecision(ruleId)
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-500" />
            {hasOverrides && !hasFreshChanges ? 'Rule overrides detected' : 'Learn from changes?'}
          </DialogTitle>
          <DialogDescription>
            {hasFreshChanges && (
              <>
                We detected{' '}
                <span className="font-semibold text-foreground">{totalChanges} new correction{totalChanges !== 1 ? 's' : ''}</span>{' '}
                on this invoice.
              </>
            )}
            {hasOverrides && hasFreshChanges && ' '}
            {hasOverrides && (
              <>
                {hasFreshChanges ? 'Additionally, you' : 'You'} changed values that were set by previously learned rules.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 flex-1 overflow-y-auto">
          {overrideHeaders.length > 0 && (
            <SectionCard
              icon={<AlertTriangle className="h-4 w-4 text-amber-500" />}
              title="Rule overrides"
              accentColor="border-l-amber-400"
            >
              <p className="text-xs text-muted-foreground mb-2">
                These corrections override previously learned rules. Choose to replace the rule or apply just this time.
              </p>
              <div className="space-y-3">
                {overrideHeaders.map((correction, i) => (
                  <div key={i} className="rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-500/5 p-2.5">
                    <HeaderCorrectionRow correction={correction} fieldOptions={fieldOptions} />
                    {correction.existing_rule_id && (
                      <OverrideDecisionToggle
                        ruleId={correction.existing_rule_id}
                        decision={getDecision(correction.existing_rule_id)}
                        onChange={handleOverrideDecision}
                      />
                    )}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {overrideClassifications.length > 0 && (
            <SectionCard
              icon={<AlertTriangle className="h-4 w-4 text-amber-500" />}
              title="Classification rule overrides"
              accentColor="border-l-amber-400"
            >
              <p className="text-xs text-muted-foreground mb-2">
                These corrections override previously learned classification rules.
              </p>
              <div className="space-y-2">
                {overrideClassifications.map((correction, i) => (
                  <div key={i} className="rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-500/5 p-2.5">
                    <ClassificationCard
                      correction={correction}
                      dealNames={dealNames}
                      accAssignments={accAssignments}
                    />
                    {correction.existing_rule_ids?.map(ruleId => (
                      <OverrideDecisionToggle
                        key={ruleId}
                        ruleId={ruleId}
                        decision={getDecision(ruleId)}
                        onChange={handleOverrideDecision}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {freshHeaders.length > 0 && (
            <SectionCard
              icon={<FileText className="h-4 w-4 text-amber-500" />}
              title="Document fields"
              accentColor="border-l-amber-400"
            >
              <div className="space-y-1.5">
                {freshHeaders.map((correction, i) => (
                  <HeaderCorrectionRow
                    key={i}
                    correction={correction}
                    fieldOptions={fieldOptions}
                  />
                ))}
              </div>
            </SectionCard>
          )}

          {learnings.merge_patterns.length > 0 && (
            <SectionCard
              icon={<GitMerge className="h-4 w-4 text-violet-500" />}
              title="Line item merges"
              accentColor="border-l-violet-400"
            >
              {learnings.merge_patterns.map((pattern, i) => (
                <div key={i} className="flex items-baseline justify-between gap-2 text-sm text-muted-foreground">
                  <span>{pattern.description}</span>
                  {pattern.source_groups.length > 0 && (
                    <span className="shrink-0 rounded-full bg-violet-100 dark:bg-violet-500/15 px-2 py-0.5 text-xs font-medium text-violet-700 dark:text-violet-300">
                      {pattern.source_groups.length} merged
                    </span>
                  )}
                </div>
              ))}
            </SectionCard>
          )}

          {learnings.price_rules.length > 0 && (
            <SectionCard
              icon={<DollarSign className="h-4 w-4 text-emerald-500" />}
              title="Price / VAT rules"
              accentColor="border-l-emerald-400"
            >
              {learnings.price_rules.map((rule, i) => (
                <div key={i} className="flex items-baseline justify-between gap-2 text-sm text-muted-foreground">
                  <span>{rule.description}</span>
                  <span className="shrink-0 rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                    {rule.affected_items}/{rule.total_items}
                  </span>
                </div>
              ))}
            </SectionCard>
          )}

          {freshClassifications.length > 0 && (
            <SectionCard
              icon={<Settings2 className="h-4 w-4 text-blue-500" />}
              title="ERP classification"
              accentColor="border-l-blue-400"
            >
              <div className="space-y-2">
                {freshClassifications.map((correction, i) => (
                  <ClassificationCard
                    key={i}
                    correction={correction}
                    dealNames={dealNames}
                    accAssignments={accAssignments}
                  />
                ))}
              </div>
            </SectionCard>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t">
          <Checkbox
            id="remember-choice"
            checked={rememberChoice}
            onCheckedChange={(checked) => setRememberChoice(checked === true)}
          />
          <label htmlFor="remember-choice" className="text-sm text-muted-foreground cursor-pointer">
            Remember my choice (can be changed in Settings)
          </label>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onConfirm(false, rememberChoice)}
            disabled={isLoading}
          >
            Don't learn
          </Button>
          <Button
            onClick={() => onConfirm(true, rememberChoice, hasOverrides ? finalDecisions : undefined)}
            disabled={isLoading}
          >
            {isLoading ? 'Approving...' : hasFreshChanges ? 'Learn & Approve' : 'Approve'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
